import { ScoreDirection, VideoRating } from '../types/generated/graphql';

// Color constants
export const COLORS = {
  GREEN: '#4caf50',
  ORANGE: '#ff9800',
  RED: '#f44336',
  RED_LIGHT: '#ff5252',
  ORANGE_DARK: '#fb8c00',
  GRAY: '#9e9e9e',
} as const;

/**
 * Get the color for a score based on its value and direction.
 *
 * For LOWER_IS_BETTER scores (e.g., sexual content detection):
 * - Low scores (1-3) = GREEN (good - little to no concerning content)
 * - Medium scores (4-6) = ORANGE (moderate concern)
 * - High scores (7-10) = RED (bad - high amount of concerning content)
 *
 * For HIGHER_IS_BETTER scores (e.g., educational value):
 * - Low scores (1-3) = RED (bad - low educational value)
 * - Medium scores (4-6) = ORANGE (moderate value)
 * - High scores (7-10) = GREEN (good - high educational value)
 *
 * @param score - The score value (typically 0-10)
 * @param scoreDirection - The direction indicating whether higher or lower is better (REQUIRED)
 * @returns Hex color string
 */
export function getScoreColor(score: number, scoreDirection: ScoreDirection): string {
  // For scores where lower is better (e.g., sexual content, violence detection)
  // Low scores are GOOD (green), high scores are BAD (red)
  if (scoreDirection === ScoreDirection.LowerIsBetter) {
    if (score <= 3) return COLORS.GREEN; // Green - low score is good
    if (score <= 6) return COLORS.ORANGE; // Orange - medium score
    return COLORS.RED; // Red - high score is bad
  }

  // For scores where higher is better (e.g., educational value, appropriateness)
  // Low scores are BAD (red), high scores are GOOD (green)
  if (score <= 3) return COLORS.RED; // Red - low score is bad
  if (score <= 6) return COLORS.ORANGE; // Orange - medium score
  return COLORS.GREEN; // Green - high score is good
}

/**
 * Get the color for a VideoRating.
 *
 * @param rating - The video rating
 * @returns Hex color string
 */
export function getRatingColor(rating: VideoRating | string): string {
  const ratingStr = String(rating);

  switch (ratingStr) {
    case 'HARMFUL':
    case VideoRating.Harmful:
      return COLORS.RED;
    case 'NEUTRAL':
    case VideoRating.Neutral:
      return COLORS.ORANGE;
    case 'BENEFICIAL':
    case VideoRating.Beneficial:
      return COLORS.GREEN;
    default:
      return COLORS.GRAY;
  }
}

/**
 * Alias for getScoreColor for backward compatibility
 */
export const getScoreBackgroundColor = getScoreColor;

// ---------------------------------------------------------------------------
// Score de-duplication
// ---------------------------------------------------------------------------

/**
 * Shape expected by `latestScoresByAgent` — any object carrying the
 * agent identity + a `createdAt` string.
 *
 * `getScoresForVideo` returns the full history for a video, including
 * every re-run of the same agent. For per-video summaries (verdict tile,
 * scorecard ribbon, agent cards) we only want the **latest** result per
 * agent specialization, otherwise superseded scores inflate the agent
 * count and can drag the displayed rating away from reality.
 *
 * "Agent identity" is `specialization` — a newer `version` of the same
 * spec supersedes older versions (it's the same check, just upgraded).
 */
export interface ScoreLike {
  agent: {specialization: string; version?: string | null};
  createdAt?: string | null;
}

/**
 * Keep only the most recent score per agent specialization.
 *
 * Deterministic tiebreaker: when `createdAt` is equal/missing, the last
 * one iterated wins — so callers passing an already-ordered list get the
 * expected "newest last" semantics. Firestore queries upstream order by
 * `createdAt desc`, but be defensive in case callers reorder.
 */
export function latestScoresByAgent<T extends ScoreLike>(scores: readonly T[]): T[] {
  const byAgent = new Map<string, T>();
  for (const s of scores) {
    const key = s.agent.specialization;
    const prev = byAgent.get(key);
    if (!prev) {
      byAgent.set(key, s);
      continue;
    }
    const prevTs = prev.createdAt ? Date.parse(prev.createdAt) : 0;
    const nextTs = s.createdAt ? Date.parse(s.createdAt) : 0;
    if (nextTs >= prevTs) byAgent.set(key, s);
  }
  return Array.from(byAgent.values());
}
